import { decodeToken as decodeOktaToken } from '@okta/okta-auth-js';

/** Issuer of Okta-issued tokens (matches the backend's OKTA_JWT_ISSUER). */
export const OKTA_ISSUER = 'https://schuettflix.okta.com';

export type JwtPayload = {
    email?: string;
    name?: string;
    given_name?: string;
    family_name?: string;
    /** Single operating market on OAuth-issued tokens (one user = one market). */
    market?: string;
    /** Okta-issued tokens carry market access as `platform-admin-<market>` group names. */
    groups?: unknown;
    iss?: string;
    [claim: string]: unknown;
};

/** UTF-8-safe payload decode — handles any JWT, including non-ASCII claims (name diacritics). */
function decodeUtf8Jwt(token: string): JwtPayload {
    const segment = token.split('.')[1];

    if (!segment) {
        throw new Error('Invalid JWT: missing payload segment');
    }

    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    // Restore base64 padding (JWT segments are stripped of it).
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    // `atob` alone yields a Latin-1 byte string and would mangle multi-byte characters.
    const bytes = Uint8Array.from(atob(padded), char => char.charCodeAt(0));

    return JSON.parse(new TextDecoder().decode(bytes)) as JwtPayload;
}

/**
 * Provider-agnostic display name. Okta id_tokens carry `name`; the monolith OAuth access
 * token carries `given_name`/`family_name` instead, so compose from those when `name` is absent.
 */
export function getDisplayName(payload: JwtPayload): string | undefined {
    if (payload.name) {
        return payload.name;
    }

    const composed = [payload.given_name, payload.family_name].filter(Boolean).join(' ');

    return composed || undefined;
}

/**
 * Decodes a JWT payload (no signature verification — the backend verifies), routing by issuer:
 * Okta-issued tokens go through Okta's own decoder (@okta/okta-auth-js), everything else through
 * the UTF-8-safe custom decoder. Both return the same payload shape, so callers don't care which
 * login path issued the token.
 */
export function decodeJwt(token: string): JwtPayload {
    const payload = decodeUtf8Jwt(token);

    if (payload.iss === OKTA_ISSUER) {
        return decodeOktaToken(token).payload;
    }

    return payload;
}
